// src/validation/dealEditValidation.ts

export interface DealEditFormData {
  deal_value: string;
  expected_closing_date: string;
  follow_up_date: string;
  due_date: string;
  deal_source_id: string;
  status: string;
  deal_priority: string;
  Description?: string; // optional
}

export interface DealEditErrors {
  deal_value?: string;
  expected_closing_date?: string;
  follow_up_date?: string;
  due_date?: string;
  deal_source_id?: string;
  status?: string;
  deal_priority?: string;
  Description?: string;
}

export const formatDealValue = (value: string) => {
  // Remove non-digits
  let cleaned = value.replace(/\D/g, "");

  // Hard limit to 15 digits
  cleaned = cleaned.slice(0, 15);

  // Auto-format (US grouping, 1000000 → 1,000,000)
  const formatted = cleaned ? Number(cleaned).toLocaleString("en-US") : "";

  return { cleaned, formatted };
};

/**
 * Pure validation function – no dependencies
 * Returns errors object (empty = valid)
 */
export const validateDealEditForm = (data: DealEditFormData): DealEditErrors => {
  const errors: DealEditErrors = {};

  // 1. Deal Value – must be positive number
  if (!data.deal_value || data.deal_value.trim() === "") {
    errors.deal_value = "Deal value is required";
  } else if (!/^\d+$/.test(data.deal_value)) {
    errors.deal_value = "Deal value must contain only numbers";
  } else if (data.deal_value.length > 15) {
    errors.deal_value = "Deal value must not exceed 15 digits";
  } else if (Number(data.deal_value) < 1) {
    errors.deal_value = "Deal value must be at least 1";
  } else if (Number(data.deal_value) > 999999999999999) {
    errors.deal_value = "Deal value cannot exceed 999,999,999,999,999";
  }

  // 2. Dates – required
  if (!data.expected_closing_date) {
    errors.expected_closing_date = "Expected closing date is required";
  }
  if (!data.follow_up_date) {
    errors.follow_up_date = "Follow up date is required";
  }
  if (!data.due_date) {
    errors.due_date = "Due date is required";
  }

  // 3. Deal Source
  if (!data.deal_source_id || data.deal_source_id === "") {
    errors.deal_source_id = "Please select a deal source";
  }

  // 4. Status
  const validStatuses = ["open", "won", "lost", "Closed"];
  if (!data.status || !validStatuses.includes(data.status)) {
    errors.status = "Please select a valid status";
  }

  // 5. Priority
  const validPriorities = ["high", "medium", "low"];
  if (!data.deal_priority || !validPriorities.includes(data.deal_priority.toLowerCase())) {
    errors.deal_priority = "Please select a priority";
  }

  // ⭐ 6. Description – NOT required, validate only if user enters something
  if (data.Description && data.Description.trim() !== "") {
    const clean = data.Description.trim();

    if (clean.length < 5) {
      errors.Description = "Description must be at least 5 characters long";
    } else if (clean.length > 150) {
      errors.Description = "Description must not exceed 150 characters";
    }
  }


  return errors;
};

export interface InvoiceFormErrors {
  deal?: string;
  invoice_date?: string;
  validation_date?: string;
  status?: string;
  notes?: string;
  terms?: string;
  split_period?: string;
}

export const validateInvoiceForm = (data: {
  selectedDeal: any;
  invoiceDate: Date | null;
  validationDate: Date | null;
  status: string;
  notes: string;
  terms: string;
  splitPeriod: string;
}) => {
  const errors: InvoiceFormErrors = {};

  const htmlPattern = /<[^>]*>/g;

  // 1. Deal Required
  if (!data.selectedDeal) {
    errors.deal = "Please select a deal";
  }

  // 2. Invoice Date Required
  if (!data.invoiceDate) {
    errors.invoice_date = "Invoice date is required";
  } else if (!(data.invoiceDate instanceof Date) || isNaN(data.invoiceDate.getTime())) {
    errors.invoice_date = "Invalid date";
  }

  // 3. Validation Date Required
  if (!data.validationDate) {
    errors.validation_date = "Validation date is required";
  } else if (!(data.validationDate instanceof Date) || isNaN(data.validationDate.getTime())) {
    errors.validation_date = "Invalid date";
  }

  // 4. validationDate > invoiceDate
  if (
    data.invoiceDate instanceof Date &&
    data.validationDate instanceof Date &&
    !isNaN(data.invoiceDate.getTime()) &&
    !isNaN(data.validationDate.getTime()) &&
    data.validationDate.getTime() < data.invoiceDate.getTime()
  ) {
    errors.validation_date = "Validation date must be greater than invoice date";
  }

  // 5. Status validation
  const validStatuses = ["paid", "notpaid", "partialpaid"];
  if (!data.status || !validStatuses.includes(data.status.toLowerCase())) {
    errors.status = "Please select invoice status";
  }

  // ⭐ Modified validateText (NO REQUIRED CHECK)
  const validateOptionalText = (
    field: string,
    fieldName: string,
    min: number,
    max: number
  ): string | undefined => {
    if (!field || field.trim() === "") return undefined; // <-- removed required

    if (htmlPattern.test(field))
      return `${fieldName} cannot contain HTML tags`;

    const clean = field.trim();

    if (clean.length < min)
      return `${fieldName} must be at least ${min} characters`;

    if (clean.length > max)
      return `${fieldName} cannot exceed ${max} characters`;

    return undefined;
  };

  // Notes – optional
  const notesError = validateOptionalText(data.notes, "Notes", 5, 150);
  if (notesError) errors.notes = notesError;

  // Terms – optional
  const termsError = validateOptionalText(data.terms, "Terms & conditions", 5, 150);
  if (termsError) errors.terms = termsError;

  // Split period – optional
  if (data.splitPeriod && data.splitPeriod.trim() !== "") {
    const cleanSplit = data.splitPeriod.trim();

    if (htmlPattern.test(cleanSplit)) {
      errors.split_period = "Split period cannot contain HTML tags";
    } else if (!/^\d+$/.test(cleanSplit)) {
      errors.split_period = "Split period must be a number only (e.g., 6)";
    } else {
      const num = parseInt(cleanSplit, 10);
      if (num < 0 || num > 12) {
        errors.split_period = "Split period must be between 1 and 12";
      }
    }
  }

  return errors;
};

export interface ProposalFormData {
  lead_id: string;
  signature_name: string;
  status: string;
  description: string;
  proposal_total_amount: number;
  Date_of_proposal?: Date | null;
  validation_date?: Date | null;
}

export interface ProposalFormErrors {
  lead?: string;
  signature_name?: string;
  status?: string;
  description?: string;
  amount?: string;
  date?: string;
  validation_date?: string;
}

export const validateProposalForm = (
  data: ProposalFormData & {
    selectedDate: Date | null;
    selectedDate1: Date | null;
  }
): ProposalFormErrors => {
  const errors: ProposalFormErrors = {};

  // 1. Lead Required
  if (!data.lead_id || data.lead_id.trim() === "") {
    errors.lead = "Please select a Lead";
  }

  const signature = data.signature_name?.trim();
  const htmlRegex = /<\/?[^>]+>/g;

  if (!signature || signature === "") {
    errors.signature_name = "Signature name is required";
  } else if (htmlRegex.test(signature)) {
    errors.signature_name = "HTML is not allowed in signature name";
  } else if (signature.length < 2) {
    errors.signature_name = "Signature name must be at least 2 characters";
  } else if (signature.length > 50) {
    errors.signature_name = "Signature name cannot exceed 50 characters";
  }

  // 3. Status
  const validStatuses = ["Draft", "Accepted", "Declined"];
  if (!data.status || !validStatuses.includes(data.status)) {
    errors.status = "Please select a valid status";
  }

  // 4. Description (OPTIONAL — ONLY validate if user enters text)
  const desc = data.description?.trim();
  if (desc) {
    if (desc.length < 3) {
      errors.description = "Description must be at least 3 characters";
    } else if (desc.length > 150) {
      errors.description = "Description cannot exceed 150 characters";
    }
  }

  // 5. Proposal Date
  if (
    !data.selectedDate ||
    !(data.selectedDate instanceof Date) ||
    isNaN(data.selectedDate.getTime())
  ) {
    errors.date = "Proposal date is required";
  }

  // 6. Validation Date
  if (
    !data.selectedDate1 ||
    !(data.selectedDate1 instanceof Date) ||
    isNaN(data.selectedDate1.getTime())
  ) {
    errors.validation_date = "Validation date is required";
  }

  // 7. Validation Date > Proposal Date
  if (
    data.selectedDate &&
    data.selectedDate1 &&
    data.selectedDate1 <= data.selectedDate
  ) {
    errors.validation_date = "Validation date must be after proposal date";
  }

  return errors;
};



export interface CreateLeadFormErrors {
  leadindustry_id?: string;
  leadowner_id?: string;
  leadsource_id?: string;
  leadtype_id?: string;
  product_id?: string;
  website_url?: string;
  lead_value?: string;
  Currency?: string;
  lead_status?: string;
  leadstage_id?: string;
  lead_description?: string;
  product_type_id?: string;
  customerPotential_id?: string;
  contact_id?: string;
  branch_id?: string;
}


export const validateCreateLeadForm = (
  data: Partial<{
    leadindustry_id: string;
    leadowner_id: string;
    leadsource_id: string;
    leadtype_id: string;
    product_id: string;
    website_url: string;
    product_type_id: string;
    customerPotential_id: string;
    lead_value: string;
    Currency: string;
    lead_status: string;
    leadstage_id: string;
    lead_description: string;
    contact_id: string;
    branch_id: string;
  }>
): CreateLeadFormErrors => {
 const errors: CreateLeadFormErrors = {};
  const htmlRegex = /<\/?[^>]+>/g;

  // 1. Contact (Student)
  if (!data.contact_id || data.contact_id.trim() === "") {
    errors.contact_id = "Student name is required";
  }

  // 2. Branch
  if (!data.branch_id || data.branch_id.trim() === "") {
    errors.branch_id = "Branch is required";
  }


  // 4. Lead Industry
  if (!data.leadindustry_id || data.leadindustry_id.trim() === "") {
    errors.leadindustry_id = "Lead Industry is required";
  }

  // 5. Lead Type
  if (!data.leadtype_id || data.leadtype_id.trim() === "") {
    errors.leadtype_id = "Lead Type is required";
  }

  // 6. Lead Stage
  if (!data.leadstage_id || data.leadstage_id.trim() === "") {
    errors.leadstage_id = "Lead Stage is required";
  }

  // 7. Product
  if (!data.product_id || data.product_id.trim() === "") {
    errors.product_id = "Product is required";
  }

  // Course Type
if (!data.product_type_id || data.product_type_id.trim() === "") {
  errors.product_type_id = "Course Type is required";
}

// Customer Potential
if (
  !data.customerPotential_id ||
  data.customerPotential_id.trim() === ""
) {
  errors.customerPotential_id = "Customer Potential is required";
}

  // 8. Lead Owner
  if (!data.leadowner_id || data.leadowner_id.trim() === "") {
    errors.leadowner_id = "Assign To (User) is required";
  }

  // 9. Quoted Value
  const val = data.lead_value?.trim();
  if (!val) {
    errors.lead_value = "Quoted Value is required";
  } else if (htmlRegex.test(val)) {
    errors.lead_value = "HTML is not allowed";
  } else if (isNaN(Number(val.replace(/,/g, "")))) {
    errors.lead_value = "Quoted Value must be a valid number";
  } else if (Number(val.replace(/,/g, "")) <= 0) {
    errors.lead_value = "Quoted Value must be greater than zero";
  }

  // 10. Currency
  if (!data.Currency || data.Currency.trim() === "") {
    errors.Currency = "Currency is required";
  }

  // 11. Lead Status
  const validStatuses = ["Not_Contacted", "Contacted", "Closed", "Lost"];
  if (!data.lead_status || !validStatuses.includes(data.lead_status)) {
    errors.lead_status = "Please select a valid Lead Status";
  }

  // 12. Website (optional)
  if (data.website_url && htmlRegex.test(data.website_url)) {
    errors.website_url = "HTML is not allowed in website URL";
  }

  // 13. Description (optional)
  const desc = data.lead_description?.trim();
  if (desc) {
    if (htmlRegex.test(desc)) {
      errors.lead_description = "HTML is not allowed in description";
    } else if (desc.length < 5) {
      errors.lead_description = "Description must be at least 5 characters";
    } else if (desc.length > 1000) {
      errors.lead_description = "Description cannot exceed 1000 characters";
    }
  }

  return errors;
};

export interface EditLeadFormErrors {
  website_url?: string;
  lead_description?: string;
  lead_status?: string;
  lead_value?: string;
  Currency?: string;
  leadtype_id?: string;
  leadsource_id?: string;
  leadindustry_id?: string;
  customerPotential_id?: string;
  leadstage_id?: string;
  user_id?: string;
  product_id?: string;
  product_type_id?: string;
  branch_id?: string;
  contact_id?: string;
  no_logins?: string;
  rate_per_login?: string;
}

export const validateEditLeadForm = (
  data: Partial<{
    website_url: string;
    lead_description: string;
    lead_status: string;
    lead_value: string;
    Currency: string;
    leadtype_id: string;
    leadsource_id: string;
    leadindustry_id: string;
    customerPotential_id: string;
    leadstage_id: string;
    user_id: string;
    product_id: string;
    product_type_id: string;
    branch_id: string;
    contact_id: string;
    no_logins: number;
    rate_per_login: number;
  }>
): EditLeadFormErrors => {

  const errors: EditLeadFormErrors = {};
  const htmlRegex = /<\/?[^>]+>/g;

  // Contact
  // if (!data.contact_id || data.contact_id.trim() === "") {
  //   errors.contact_id = "Student name is required";
  // }

  // // Branch
  // if (!data.branch_id || data.branch_id.trim() === "") {
  //   errors.branch_id = "Branch is required";
  // }

  // // Lead Industry
  // if (!data.leadindustry_id || data.leadindustry_id.trim() === "") {
  //   errors.leadindustry_id = "Lead Industry is required";
  // }

  // // Lead Type
  // if (!data.leadtype_id || data.leadtype_id.trim() === "") {
  //   errors.leadtype_id = "Lead Type is required";
  // }

  // // Lead Stage
  // if (!data.leadstage_id || data.leadstage_id.trim() === "") {
  //   errors.leadstage_id = "Lead Stage is required";
  // }

  // // Product
  // if (!data.product_id || data.product_id.trim() === "") {
  //   errors.product_id = "Product is required";
  // }

  // // Product Type
  // if (!data.product_type_id || data.product_type_id.trim() === "") {
  //   errors.product_type_id = "Product Type is required";
  // }

  // // Customer Potential
  // if (!data.customerPotential_id || data.customerPotential_id.trim() === "") {
  //   errors.customerPotential_id = "Customer Potential is required";
  // }

  // // Assign To (User)
  // if (!data.user_id || data.user_id.trim() === "") {
  //   errors.user_id = "Assign To (User) is required";
  // }

  // Lead Value
  const val = data.lead_value?.trim();

// If empty → skip validation (no required rule)
if (val) {
  if (htmlRegex.test(val)) {
    errors.lead_value = "HTML is not allowed";
  } else if (isNaN(Number(val.replace(/,/g, "")))) {
    errors.lead_value = "Quoted Value must be a valid number";
  } else if (Number(val.replace(/,/g, "")) <= 0) {
    errors.lead_value = "Quoted Value must be greater than zero";
  }
}


  // Currency
  // if (!data.Currency || data.Currency.trim() === "") {
  //   errors.Currency = "Currency is required";
  // }

  //Lead Status
  const validStatuses = ["Not_Contacted", "Contacted", "Closed", "Lost"];
  if (!data.lead_status || !validStatuses.includes(data.lead_status)) {
    errors.lead_status = "Please select a valid Lead Status";
  }

  // Website URL (optional)
  if (data.website_url && htmlRegex.test(data.website_url)) {
    errors.website_url = "HTML is not allowed in website URL";
  }

  // Description (optional)
  const desc = data.lead_description?.trim();
  if (desc) {
    if (htmlRegex.test(desc)) {
      errors.lead_description = "HTML is not allowed in description";
    } else if (desc.length < 5) {
      errors.lead_description = "Description must be at least 5 characters";
    } else if (desc.length > 1000) {
      errors.lead_description = "Description cannot exceed 1000 characters";
    }
  }

  // No. of Logins
  if (data.no_logins !== undefined) {
    if (isNaN(Number(data.no_logins)) || Number(data.no_logins) < 0) {
      errors.no_logins = "No. of logins must be 0 or a positive number";
    }
  }

  // Rate Per Login
  if (data.rate_per_login !== undefined) {
    if (isNaN(Number(data.rate_per_login)) || Number(data.rate_per_login) < 0) {
      errors.rate_per_login = "Rate per login must be 0 or a positive number";
    }
  }

  return errors;
};



// ==================== INTERFACES ====================
export interface Branch {
  branch_name?: string;
  branch_address?: string;
  branch_city?: string;
  branch_state?: string;
  branch_country?: string;
  branch_zipcode?: string;
  contact_address?: string;
  contact_city?: string;
  contact_state?: string;
  contact_country?: string;
  contact_zipcode?: string;
  gst_number?: string;
}

export interface ContactFormData {
  contact_Company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_mail?: string;
  contact_desigination?: string;
  contact_description?: string;
  website_url?: string;
  lead_from?: string;
  fax?: string;
  no_of_employee?: string;
  rating?: string;
  linkedin_url?: string;
  call_date1?: string;
  disposition1?: string;
  call_date2?: string;
  disposition2?: string;
  email_marketing_date1?: string;
  email_status1?: string;
  email_marketing_date2?: string;
  email_status2?: string;

  // Branches
  branches?: Branch[];
  address_mode?: "single" | "multiple";
  contact_city?: string;
  contact_address?: string;
  contact_state?: string;
  contact_country?: string;
  contact_zipcode?: string;
  gst_number?: string;
}

export interface ContactFormErrors {
  contact_Company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_mail?: string;
  contact_desigination?: string;
  contact_description?: string;
  website_url?: string;
  lead_from?: string;
  fax?: string;
  no_of_employee?: string;
  rating?: string;
  linkedin_url?: string;
  address?: string; // For branch validation
}

const htmlRegex = /<\/?[^>]+>/gi;
const phoneRegex = /^(\+91|0)?[6-9]\d{9}$/; // Indian mobile: 9876543210, +919876543210, 09876543210
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const urlRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

export const validateContactForm = (data: ContactFormData): ContactFormErrors => {
  const errors: ContactFormErrors = {};

  const htmlRegex = /<\/?[^>]+>/gi;
  const phoneRegex = /^(\+91|0)?[6-9]\d{9}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

  const required = (value: string | undefined, field: string, key: keyof ContactFormErrors) => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      errors[key] = `${field} is required`;
    }
    return trimmed;
  };

  const optional = (value: string | undefined, field: string, key: keyof ContactFormErrors) => {
    const trimmed = (value || "").trim();
    if (trimmed && htmlRegex.test(trimmed)) {
      errors[key] = `HTML not allowed in ${field.toLowerCase()}`;
    }
    return trimmed;
  };

  // === REQUIRED FIELDS ===
  required(data.contact_Company_name, "Company Name", "contact_Company_name");
  required(data.contact_name, "Contact Person Name", "contact_name");
  required(data.lead_from, "Lead Source", "lead_from");

  // === PHONE - REQUIRED & VALID ===
  const phone = (data.contact_phone || "").replace(/\s+/g, "");
  if (!phone) {
    errors.contact_phone = "Phone number is required";
  } else if (!phoneRegex.test(phone)) {
    errors.contact_phone = "Invalid phone number";
  }

  // === OPTIONAL FIELDS (HTML ONLY BLOCKED) ===
  optional(data.contact_mail, "Email", "contact_mail");
  optional(data.contact_desigination, "Designation", "contact_desigination");
  optional(data.contact_description, "Remarks", "contact_description");
  optional(data.website_url, "Website", "website_url");
  optional(data.fax, "Fax", "fax");
  optional(data.no_of_employee, "No. of Employees", "no_of_employee");
  optional(data.rating, "Rating", "rating");
  optional(data.linkedin_url, "LinkedIn URL", "linkedin_url");

  // === WEBSITE OPTIONAL BUT VALID URL IF ENTERED ===
  const website = (data.website_url || "").trim();
  if (website && !urlRegex.test(website)) {
    errors.website_url = "Enter a valid URL (example: https://example.com)";
  }

 // === CITY REQUIRED - SUPPORT BOTH SINGLE & MULTIPLE MODE ===
const branches = data.branches || [];

// Determine if city is filled based on address mode
let hasValidCity = false;

if (data.address_mode === "single") {
  const city = (data as any).contact_city?.trim();
  hasValidCity = !!city;
  
  // Optional: Check for HTML in single mode
  if (city && htmlRegex.test(city)) {
    errors.address = "HTML or script tags are not allowed in City field";
  }
} 
else if (data.address_mode === "multiple" && branches.length > 0) {
  // At least ONE branch must have a non-empty city
  const validBranch = branches.find(b => b.contact_city?.trim());
  hasValidCity = !!validBranch;

  // Optional: Check all filled cities for HTML
  for (const branch of branches) {
    const city = branch.contact_city?.trim();
    if (city && htmlRegex.test(city)) {
      errors.address = "HTML or script tags are not allowed in City field";
      break;
    }
  }
}

// If no valid city found → show required error
if (!hasValidCity) {
  errors.address = "City is required";
}

  return errors;
};


export const validateEditContactForm = (data: ContactFormData): ContactFormErrors => {
  const errors: ContactFormErrors = {};

  const htmlRegex = /<\/?[^>]+>/gi;
  const phoneRegex = /^(\+91|0)?[6-9]\d{9}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

  const required = (value: string | undefined, field: string, key: keyof ContactFormErrors) => {
    const trimmed = (value || "").trim();
    if (!trimmed) errors[key] = `${field} is required`;
    return trimmed;
  };

  const optional = (value: string | undefined, field: string, key: keyof ContactFormErrors) => {
    const trimmed = (value || "").trim();
    if (trimmed && htmlRegex.test(trimmed)) {
      errors[key] = `${field} cannot contain HTML`;
    }
    return trimmed;
  };

  // === REQUIRED FIELDS ===
  required(data.contact_Company_name, "Company Name", "contact_Company_name");
  required(data.contact_name, "Contact Person Name", "contact_name");
  required(data.lead_from, "Lead Source", "lead_from");

  // === PHONE CHECK ===
  const phone = (data.contact_phone || "").replace(/\s+/g, "");
  if (!phone) errors.contact_phone = "Phone is required";
  else if (!phoneRegex.test(phone)) errors.contact_phone = "Invalid phone number";

  // === OPTIONAL FIELDS ===
  optional(data.contact_mail, "Email", "contact_mail");
  optional(data.contact_desigination, "Designation", "contact_desigination");
  optional(data.contact_description, "Remarks", "contact_description");
  optional(data.website_url, "Website URL", "website_url");

  // === VALIDATE WEBSITE URL ===
  const site = (data.website_url || "").trim();
  if (site && !urlRegex.test(site)) errors.website_url = "Enter a valid URL";

  // ==== CITY VALIDATION ====  
  const branches = data.branches || [];
  let hasValidCity = false;

  if (data.address_mode === "single") {
    const city = data.contact_city?.trim();
    hasValidCity = !!city;

    if (city && htmlRegex.test(city)) {
      errors.address = "HTML not allowed in City";
    }
  } else if (data.address_mode === "multiple") {
    const oneBranch = branches.find((b) => b.branch_city?.trim());
    hasValidCity = !!oneBranch;

    for (const b of branches) {
      const city = b.branch_city?.trim();
      if (city && htmlRegex.test(city)) {
        errors.address = "HTML not allowed in City fields";
        break;
      }
    }
  }

  if (!hasValidCity) errors.address = "City is required";

  return errors;
};


export const validateImportName = (value: string): string | undefined => {
  const trimmed = value.trim();

  // Required / not only spaces
  if (!trimmed) {
    return "Import name is required";
  }

  // Min length
  if (trimmed.length < 3) {
    return "Import name must be at least 3 characters";
  }

  // Max length
  if (trimmed.length > 50) {
    return "Import name cannot exceed 50 characters";
  }

  // Allowed characters only
  const allowedRegex = /^[A-Za-z0-9@$&\/\-_. ]+$/;
  if (!allowedRegex.test(trimmed)) {
    return "Import name contains invalid characters";
  }

  // Not only numbers
  if (/^\d+$/.test(trimmed)) {
    return "Import name cannot be only numbers";
  }

  return undefined;
};